import React, { useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import RequestTrainingButton from '@/components/competency/RequestTrainingButton';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Descriptions,
  Form,
  Input,
  Progress,
  Steps,
  message,
  Modal,
  Select,
  Alert,
  Checkbox,
  Divider,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EditOutlined,
  FileOutlined,
  PlayCircleOutlined,
  PrinterOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  useCAPA,
  useCloseCAPA,
  useUpdateCAPA,
  useUpdateCAPAStatus,
  useVerifyCAPAEffectiveness,
  useSubmitCAPAForApproval,
  qualityKeys,
} from '../../hooks/useQuality';
import DocumentList from '../../components/documents/DocumentList';
import DocumentUpload from '../../components/documents/DocumentUpload';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_CAPA_RECORD } from '@/constants/approval-entity-types';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const CAPADetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [closeForm] = Form.useForm();
  const [effectivenessForm] = Form.useForm();
  const [isClosing, setIsClosing] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isEffectivenessVisible, setIsEffectivenessVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  const { data: capa, isLoading, refetch } = useCAPA(id || '');
  const closeMutation = useCloseCAPA();
  const updateMutation = useUpdateCAPA();
  const updateStatusMutation = useUpdateCAPAStatus();
  const verifyMutation = useVerifyCAPAEffectiveness();
  const submitApprovalMutation = useSubmitCAPAForApproval();

  const statusTransitions: Record<string, string[]> = {
    Assigned: ['In Progress', 'Cancelled'],
    'In Progress': ['Completed', 'Assigned', 'Cancelled'],
    Completed: ['Effectiveness Verification', 'In Progress'],
    'Pending Approval': [],
    'Effectiveness Verification': ['Closed', 'In Progress', 'Completed'],
    Closed: [],
    Cancelled: ['Assigned'],
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Assigned: 'blue',
      'In Progress': 'processing',
      Completed: 'success',
      'Pending Approval': 'gold',
      'Effectiveness Verification': 'purple',
      Closed: 'success',
      Cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getCurrentStep = () => {
    const statusMap: Record<string, number> = {
      Assigned: 0,
      'In Progress': 1,
      Completed: 2,
      'Pending Approval': 2,
      'Effectiveness Verification': 3,
      Closed: 4,
      Cancelled: 0,
    };
    return statusMap[capa?.status || 'Assigned'] || 0;
  };

  const steps = useMemo(
    () => [
      { title: 'Assigned', description: 'CAPA assigned' },
      { title: 'In Progress', description: 'Action in progress' },
      { title: 'Completed', description: 'Action completed' },
      { title: 'Verification', description: 'Effectiveness verification' },
      { title: 'Closed', description: 'CAPA closed' },
    ],
    []
  );

  const actionSteps = Array.isArray(capa?.action_steps) ? capa!.action_steps : [];

  const toggleStepDone = async (index: number, done: boolean) => {
    if (!capa || !id) return;
    const next = actionSteps.map((s: any, i: number) =>
      i === index ? { ...s, done } : s
    );
    const doneCount = next.filter((s: any) => s.done).length;
    const pct =
      next.length > 0 ? Math.round((doneCount / next.length) * 100) : capa.progress_percentage;
    await updateMutation.mutateAsync({
      id,
      data: { action_steps: next, progress_percentage: pct },
    });
    refetch();
  };

  const handleClose = async (values: any) => {
    try {
      await closeMutation.mutateAsync({ id: id!, notes: values.notes });
      setIsClosing(false);
      refetch();
    } catch (error) {
      console.error('Error closing CAPA:', error);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedStatus) return;
    try {
      await updateStatusMutation.mutateAsync({ id: id!, status: selectedStatus });
      setIsStatusModalVisible(false);
      setSelectedStatus('');
      refetch();
    } catch {
      /* hook toast */
    }
  };

  const handleEffectiveness = async (values: any) => {
    try {
      await verifyMutation.mutateAsync({
        id: id!,
        data: {
          effectiveness_verified: !!values.effectiveness_verified,
          effectiveness_notes: values.effectiveness_notes,
          effectiveness_rating: values.effectiveness_rating,
          effectiveness_criteria: values.effectiveness_criteria,
        },
      });
      setIsEffectivenessVisible(false);
      refetch();
    } catch {
      /* hook toast */
    }
  };

  if (isLoading) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  if (!capa) {
    return <div style={{ padding: 24 }}>CAPA not found</div>;
  }

  const isOverdue =
    capa.target_completion_date &&
    new Date(capa.target_completion_date) < new Date() &&
    capa.status !== 'Closed' &&
    capa.status !== 'Cancelled';

  const allowedNext = statusTransitions[capa.status] || [];
  const ncr = (capa as any).ncr;
  const canSubmitApproval =
    Boolean(id) &&
    !capa.approval_instance_id &&
    capa.status !== 'Closed' &&
    capa.status !== 'Cancelled' &&
    capa.status !== 'Pending Approval' &&
    (capa.status === 'Completed' ||
      (capa.status === 'In Progress' && Boolean(capa.action_plan?.trim())));
  const showApproval =
    capa.status === 'Pending Approval' || Boolean(capa.approval_instance_id);

  return (
    <div style={{ padding: 24 }}>
      {id ? <AiPageContextSync module="quality" docType="CAPA" docId={id} /> : null}
      <Space style={{ marginBottom: 24 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/capas')}>
          Back
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          CAPA: {capa.capa_number}
        </Title>
        <Tag color={getStatusColor(capa.status)}>{capa.status}</Tag>
        {isOverdue && <Tag color="red">Overdue</Tag>}
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          {capa.status !== 'Closed' &&
            capa.status !== 'Cancelled' &&
            capa.status !== 'Pending Approval' && (
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/qms/capas/${id}/edit`)}
            >
              Edit
            </Button>
          )}
          {allowedNext.length > 0 && (
            <Button
              icon={<PlayCircleOutlined />}
              onClick={() => {
                setSelectedStatus(allowedNext[0]);
                setIsStatusModalVisible(true);
              }}
            >
              Change Status
            </Button>
          )}
          {canSubmitApproval && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={submitApprovalMutation.isPending}
              onClick={() => submitApprovalMutation.mutate(id!)}
            >
              Submit for approval
            </Button>
          )}
          <RequestTrainingButton sourceType="CAPA" sourceId={capa.id} defaultNotes={capa.capa_number} />
          {(capa.status === 'Completed' ||
            capa.status === 'Effectiveness Verification') &&
            !capa.effectiveness_verified && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  effectivenessForm.setFieldsValue({
                    effectiveness_criteria: capa.effectiveness_criteria,
                    effectiveness_verified: true,
                  });
                  setIsEffectivenessVisible(true);
                }}
              >
                Verify Effectiveness
              </Button>
            )}
          {capa.status !== 'Closed' &&
            capa.status !== 'Cancelled' &&
            capa.status !== 'Pending Approval' && (
            <Button danger icon={<CheckCircleOutlined />} onClick={() => setIsClosing(true)}>
              Close CAPA
            </Button>
          )}
          <Button
            icon={<PrinterOutlined />}
            onClick={() => window.open(`/print/qms/capas/${id}`, '_blank')}
          >
            Print
          </Button>
        </Space>
      </Card>

      {capa.status === 'Pending Approval' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Pending approval"
          description="This CAPA is awaiting approval. Use the Approval panel or Approval Inbox to approve or reject."
        />
      )}

      {showApproval && id && (
        <Card title="Approval" style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_CAPA_RECORD}
            entityId={id}
            extraInvalidateQueryKeys={[qualityKeys.capa(id), qualityKeys.capas()]}
          />
        </Card>
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Text type="secondary">Type</Text>
            <div>
              <Text strong>{capa.type}</Text>
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <Text type="secondary">Source</Text>
            <div>
              <Text>{capa.source}</Text>
              {capa.source_reference ? (
                <Text type="secondary"> · {capa.source_reference}</Text>
              ) : null}
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Text type="secondary">Progress</Text>
            <Progress percent={capa.progress_percentage || 0} style={{ marginTop: 4 }} />
            <Text type="secondary">Target</Text>
            <div>
              {capa.target_completion_date
                ? dayjs(capa.target_completion_date).format('DD/MM/YYYY')
                : '—'}
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            {capa.ncr_id ? (
              <>
                <Text type="secondary">Linked NCR</Text>
                <div>
                  <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/ncrs/${capa.ncr_id}`)}>
                    {ncr?.ncr_number || 'View NCR'}
                  </Button>
                </div>
                {ncr?.description && (
                  <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginTop: 8, marginBottom: 0 }}>
                    {ncr.description}
                  </Paragraph>
                )}
                {ncr?.disposition && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Disposition: </Text>
                    <Tag>{ncr.disposition}</Tag>
                  </div>
                )}
              </>
            ) : (
              <Text type="secondary">No linked NCR</Text>
            )}
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Steps current={getCurrentStep()} items={steps} />
      </Card>

      <Card title="Details" style={{ marginBottom: 16 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="CAPA Number" span={2}>
            {capa.capa_number}
          </Descriptions.Item>
          <Descriptions.Item label="Department" span={2}>
            {capa.department_name || capa.department_id || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {capa.description}
          </Descriptions.Item>
          {capa.problem_statement && (
            <Descriptions.Item label="Problem statement" span={2}>
              {capa.problem_statement}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Action plan" span={2}>
            {capa.action_plan}
          </Descriptions.Item>
          <Descriptions.Item label="Responsible">
            {capa.responsible_person_name || capa.responsible_person_id || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Actual completion">
            {capa.actual_completion_date
              ? dayjs(capa.actual_completion_date).format('DD/MM/YYYY')
              : '—'}
          </Descriptions.Item>
          {capa.effectiveness_criteria && (
            <Descriptions.Item label="Effectiveness criteria" span={2}>
              {capa.effectiveness_criteria}
            </Descriptions.Item>
          )}
        </Descriptions>

        {actionSteps.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Text strong>Action steps</Text>
            <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
              {actionSteps.map((step: any, index: number) => (
                <Checkbox
                  key={index}
                  checked={!!step.done}
                  disabled={capa.status === 'Closed'}
                  onChange={(e) => toggleStepDone(index, e.target.checked)}
                >
                  {step.step || step.description || `Step ${index + 1}`}
                </Checkbox>
              ))}
            </Space>
            <Button
              type="link"
              style={{ paddingLeft: 0, marginTop: 8 }}
              onClick={() => navigate(`/qms/capas/${id}/edit`)}
            >
              Edit steps on form
            </Button>
          </div>
        )}

        {capa.effectiveness_verified && (
          <Alert
            style={{ marginTop: 24 }}
            type="success"
            showIcon
            message={`Effectiveness: ${capa.effectiveness_rating || 'Verified'}`}
            description={capa.effectiveness_notes || undefined}
          />
        )}
      </Card>

      <Card
        title={
          <span>
            <FileOutlined /> Documents / evidence
          </span>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <DocumentUpload
            entityType="CAPARecord"
            entityId={id || ''}
            buttonText="Upload CAPA evidence"
            defaultValues={{
              document_type: 'Other',
              document_category: 'CAPA Evidence',
              tags: 'capa,evidence',
            }}
            onSuccess={() => message.success('Document uploaded')}
          />
        </div>
        <DocumentList entityType="CAPARecord" entityId={id || ''} />
      </Card>

      <Modal
        title="Change CAPA Status"
        open={isStatusModalVisible}
        onCancel={() => setIsStatusModalVisible(false)}
        onOk={handleStatusChange}
        confirmLoading={updateStatusMutation.isPending}
      >
        <Form layout="vertical">
          <Form.Item label="New status" required>
            <Select
              value={selectedStatus || undefined}
              onChange={setSelectedStatus}
              options={allowedNext.map((s) => ({ value: s, label: s }))}
              placeholder="Select status"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Verify CAPA Effectiveness"
        open={isEffectivenessVisible}
        onCancel={() => setIsEffectivenessVisible(false)}
        onOk={() => effectivenessForm.submit()}
        confirmLoading={verifyMutation.isPending}
        width={640}
      >
        <Form form={effectivenessForm} layout="vertical" onFinish={handleEffectiveness}>
          <Form.Item name="effectiveness_criteria" label="Criteria">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="effectiveness_rating" label="Rating" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'Effective', label: 'Effective' },
                { value: 'Partially Effective', label: 'Partially Effective' },
                { value: 'Not Effective', label: 'Not Effective' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="effectiveness_verified"
            label="Verified"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox>Mark as verified</Checkbox>
          </Form.Item>
          <Form.Item name="effectiveness_notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Close CAPA"
        open={isClosing}
        onCancel={() => setIsClosing(false)}
        onOk={() => closeForm.submit()}
        confirmLoading={closeMutation.isPending}
      >
        <Form form={closeForm} layout="vertical" onFinish={handleClose}>
          <Form.Item name="notes" label="Closure notes">
            <TextArea rows={4} placeholder="Enter closure notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CAPADetailPage;
